import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { useLang } from "../lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDay(dateIso, lang) {
  const d = new Date(`${dateIso}T12:00:00`);
  return {
    dow: new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "short" }).format(d),
    day: d.getDate(),
    month: new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", { month: "short" }).format(d),
  };
}

export default function BookingModal({ open, onOpenChange }) {
  const { lang, t } = useLang();
  const tb = t.booking;
  const [step, setStep] = useState(1);
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setDate(null);
    setSlot(null);
    setError("");
    setLoadingDays(true);
    axios
      .get(`${API}/availability?days=12`)
      .then((r) => {
        setDays(r.data.days);
        const first = r.data.days[0];
        if (first) setDate(first.date);
      })
      .catch(() => setDays([]))
      .finally(() => setLoadingDays(false));
  }, [open]);

  const selectedDay = useMemo(() => days.find((d) => d.date === date), [days, date]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/bookings`, { ...form, date, slot, lang });
      setStep(3);
      toast.success(tb.successTitle);
    } catch (e) {
      const status = e.response?.status;
      setError(status === 409 ? tb.conflict : tb.error);
      if (status === 409) {
        const r = await axios.get(`${API}/availability?days=12`);
        setDays(r.data.days);
        setSlot(null);
        setStep(1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border border-edge bg-surface px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-signal focus:outline-none transition-colors duration-200";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl rounded-none border-edge bg-void p-0 text-foreground"
        data-testid="booking-modal"
      >
        <div className="border-b border-edge px-8 py-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-semibold tracking-tight">{tb.title}</DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {tb.tz}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-2" data-testid="booking-steps">
            {[1, 2, 3].map((s) => (
              <span
                key={s}
                className={`h-0.5 flex-1 transition-colors duration-300 ${step >= s ? "bg-signal" : "bg-edge"}`}
              />
            ))}
          </div>
        </div>

        <div className="px-8 py-8">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
              >
                <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  01 — {tb.step1}
                </p>
                {loadingDays ? (
                  <div className="flex h-40 items-center justify-center" data-testid="booking-loading">
                    <Loader2 className="h-6 w-6 animate-spin text-signal" />
                  </div>
                ) : (
                  <>
                    <div className="mb-6 flex flex-wrap gap-2" data-testid="booking-days">
                      {days.map((d) => {
                        const f = formatDay(d.date, lang);
                        return (
                          <button
                            key={d.date}
                            onClick={() => {
                              setDate(d.date);
                              setSlot(null);
                            }}
                            data-testid={`booking-day-${d.date}`}
                            className={`flex flex-col items-center border px-4 py-3 transition-colors duration-200 ${
                              date === d.date
                                ? "border-signal bg-signal/10 text-foreground"
                                : "border-edge text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <span className="font-mono text-[10px] uppercase tracking-[0.15em]">{f.dow}</span>
                            <span className="font-display text-xl font-semibold">{f.day}</span>
                            <span className="font-mono text-[10px] uppercase">{f.month}</span>
                          </button>
                        );
                      })}
                    </div>
                    {selectedDay && (
                      <div className="mb-8 grid grid-cols-4 gap-2" data-testid="booking-slots">
                        {selectedDay.slots.map((s) => (
                          <button
                            key={s.time}
                            disabled={!s.available}
                            onClick={() => setSlot(s.time)}
                            data-testid={`booking-slot-${s.time.replace(":", "")}`}
                            className={`border px-3 py-3 font-mono text-sm transition-colors duration-200 ${
                              !s.available
                                ? "cursor-not-allowed border-edge/40 text-muted-foreground/30 line-through"
                                : slot === s.time
                                  ? "border-signal bg-signal text-white"
                                  : "border-edge text-foreground hover:border-signal"
                            }`}
                          >
                            {s.time}
                          </button>
                        ))}
                      </div>
                    )}
                    {error && (
                      <p className="mb-4 font-mono text-xs text-signal" data-testid="booking-error">
                        {error}
                      </p>
                    )}
                    <button
                      onClick={() => setStep(2)}
                      disabled={!slot}
                      data-testid="booking-next-btn"
                      className="group flex w-full items-center justify-center gap-3 bg-foreground py-4 font-mono text-xs uppercase tracking-[0.15em] text-void transition-colors duration-200 enabled:hover:bg-signal enabled:hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      {tb.next}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
              >
                <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  02 — {tb.step2} · {date} {slot}
                </p>
                <div className="space-y-4" data-testid="booking-form">
                  <input
                    className={inputCls}
                    placeholder={tb.name}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="booking-name-input"
                  />
                  <input
                    className={inputCls}
                    type="email"
                    placeholder={tb.email}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="booking-email-input"
                  />
                  <input
                    className={inputCls}
                    placeholder={tb.company}
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    data-testid="booking-company-input"
                  />
                  <textarea
                    className={`${inputCls} min-h-24 resize-none`}
                    placeholder={tb.message}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="booking-message-input"
                  />
                </div>
                {error && (
                  <p className="mt-4 font-mono text-xs text-signal" data-testid="booking-error">
                    {error}
                  </p>
                )}
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    data-testid="booking-back-btn"
                    className="flex items-center gap-2 border border-edge px-5 py-4 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:border-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {tb.back}
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting || !form.name.trim() || !form.email.trim()}
                    data-testid="booking-submit-btn"
                    className="flex flex-1 items-center justify-center gap-3 bg-signal py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-200 enabled:hover:bg-signalhover disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {submitting ? tb.loading : tb.submit}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-start gap-6 py-6"
                data-testid="booking-success"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-signal">
                  <Check className="h-6 w-6 text-signal" />
                </span>
                <div>
                  <h3 className="font-display text-3xl font-semibold tracking-tight">{tb.successTitle}</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                    {tb.successBody}
                  </p>
                  <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-signal">
                    {date} — {slot} (Europe/Paris)
                  </p>
                </div>
                <button
                  onClick={() => onOpenChange(false)}
                  data-testid="booking-close-btn"
                  className="border border-edge px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:border-foreground hover:text-foreground"
                >
                  ×
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
